import { z } from 'zod'

/**
 * Canonical metric result envelope — PRD §9 and adopted C11 (P17-07 resolution, 2026-09-24).
 * One shape preserves each distinct claim: `scope` names local versus connected authority,
 * `population` names the counted cohort, `window` names the calculation interval, and
 * `retained_bounds` names the available history. No alias fields can contradict them.
 *
 * `null` means unavailable / not applicable / insufficient sample; `0` is a known none.
 */

const IsoDateTime = z.string().datetime()

export const MetricUnit = z.enum([
  'count',
  'percent',
  'milliseconds',
  'bytes',
  'status',
  'estimate',
  'items',
])
export type MetricUnit = z.infer<typeof MetricUnit>

export const MetricCompleteness = z.enum(['complete', 'partial', 'insufficient', 'not_applicable'])
export type MetricCompleteness = z.infer<typeof MetricCompleteness>

export const MetricScope = z.enum(['local', 'connected'])
export type MetricScope = z.infer<typeof MetricScope>

/** C11 window metadata. */
export const MetricWindow = z
  .object({
    start: IsoDateTime,
    end: IsoDateTime,
  })
  .strict()
export type MetricWindow = z.infer<typeof MetricWindow>

/** C11 retained-bounds metadata (the retention horizon the value was computed from). */
export const MetricRetainedBounds = z
  .object({
    from: IsoDateTime.nullable(),
    to: IsoDateTime.nullable(),
  })
  .strict()
export type MetricRetainedBounds = z.infer<typeof MetricRetainedBounds>

/** Authorized drill-down scope: filters a caller is entitled to narrow by (C11). */
export const MetricDrilldownScope = z
  .object({
    authorized: z.boolean(),
    filters: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  })
  .strict()
export type MetricDrilldownScope = z.infer<typeof MetricDrilldownScope>

export const MetricResultEnvelope = z
  .object({
    metric_id: z.string().min(1).max(64),
    definition_version: z.number().int().positive(),
    value: z.json(),
    unit: MetricUnit,
    scope: MetricScope,
    as_of: IsoDateTime,
    window: MetricWindow.nullable(),
    timezone: z.string().min(1).max(64),
    population: z.string().min(1).max(200),
    sample_size: z.number().int().nonnegative().nullable(),
    excluded_count: z.number().int().nonnegative().nullable(),
    unknown_count: z.number().int().nonnegative().nullable(),
    completeness: MetricCompleteness,
    retained_bounds: MetricRetainedBounds,
    stale: z.boolean(),
    provisional: z.boolean(),
    drilldown_scope: MetricDrilldownScope,
    notes: z.array(z.string().max(400)),
    numerator: z.number().nullable().optional(),
    denominator: z.number().nullable().optional(),
  })
  .strict()
  .superRefine((metric, ctx) => {
    if (
      metric.numerator !== undefined &&
      metric.numerator !== null &&
      metric.denominator === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'a numerator is reported with its denominator',
        path: ['denominator'],
      })
    }
    if (metric.denominator === 0 && metric.value !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'a zero denominator yields no rate, never a fabricated value',
        path: ['value'],
      })
    }
    if (metric.window !== null && Date.parse(metric.window.start) > Date.parse(metric.window.end)) {
      ctx.addIssue({
        code: 'custom',
        message: 'window start cannot follow its end',
        path: ['window'],
      })
    }
  })
export type MetricResultEnvelope = z.infer<typeof MetricResultEnvelope>
